import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await api.get('/api/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          // Clear invalid token
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (emailOrUsername, password) => {
    try {
      console.log('Attempting login with:', emailOrUsername);
      const response = await api.post('/api/auth/login', {
        emailOrUsername,
        password
      });
      
      console.log('Login response:', response.data);
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      if (userData?.isFirstLogin) {
        localStorage.setItem('first_login_onboarding_pending', '1');
      }
      
      console.log('Login successful, user set:', userData);
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/api/auth/register', userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const requestPhoneLogin = async (phoneNumber) => {
    try {
      const response = await api.post('/api/auth/login/phone', {
        phoneNumber
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to send OTP' 
      };
    }
  };

  const verifyOTP = async (phoneNumber, otpCode) => {
    try {
      const response = await api.post('/api/auth/verify-otp', {
        phoneNumber,
        otpCode
      });
      
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      if (userData?.isFirstLogin) {
        localStorage.setItem('first_login_onboarding_pending', '1');
      }
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Invalid OTP' 
      };
    }
  };

  const requestEmailLogin = async (email) => {
    try {
      const response = await api.post('/api/auth/login/email', {
        email
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to send login code' 
      };
    }
  };

  const verifyEmailCode = async (email, code) => {
    try {
      const response = await api.post('/api/auth/verify-email-code', {
        email,
        code
      });
      
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      if (userData?.isFirstLogin) {
        localStorage.setItem('first_login_onboarding_pending', '1');
      }
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Invalid code' 
      };
    }
  };

  const googleLogin = async (googleData) => {
    try {
      const response = await api.post('/api/auth/google-login', googleData);
      
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      if (userData?.isFirstLogin) {
        localStorage.setItem('first_login_onboarding_pending', '1');
      }
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Google login failed' 
      };
    }
  };

  const verifyEmail = async (token) => {
    try {
      const response = await api.post(`/api/auth/verify-email?token=${token}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Email verification failed' 
      };
    }
  };

  const verifyPhone = async (phoneNumber, otpCode) => {
    try {
      const response = await api.post('/api/auth/verify-phone', {
        phoneNumber,
        otpCode
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Phone verification failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const isAdmin = () => {
    return user && user.role === 'ADMIN';
  };

  const isAuthenticated = useMemo(() => {
    return () => !!user && !!token;
  }, [user, token]);

  const value = useMemo(() => ({
    user,
    setUser,
    token,
    loading,
    login,
    register,
    requestPhoneLogin,
    verifyOTP,
    requestEmailLogin,
    verifyEmailCode,
    googleLogin,
    verifyEmail,
    verifyPhone,
    logout,
    isAdmin,
    isAuthenticated
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, token, loading, isAuthenticated]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

